import { useState, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Alert,
  Snackbar,
  Paper,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Chip,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Add, EditOutlined, DeleteOutlined, Mail, Close, Person } from '@mui/icons-material';
import { correspondenceTypeService } from '../../correspondence-types';
import CorrespondenceTypeModalForm from '../../correspondence-types/components/CorrespondenceTypeModalForm';
import { companyService } from '../index';

const CompanyCorrespondenceTypes = ({ companyId }) => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [detailType, setDetailType] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    loadTypes();
    loadCompanies();
  }, [companyId]);

  const loadCompanies = async () => {
    try {
      const response = await companyService.getAll({ limit: 100 });
      setCompanies(response.data);
    } catch (error) {
      console.error('Error al cargar empresas:', error);
    }
  };

  const loadTypes = async () => {
    try {
      setLoading(true);
      const response = await correspondenceTypeService.getAll({ companyId, limit: 100 });
      setTypes(response.data);
    } catch (error) {
      showSnackbar('Error al cargar tipos de correspondencia', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setSelectedType(null);
    setOpenModal(true);
  };

  const handleEdit = (type) => {
    setSelectedType(type);
    setOpenModal(true);
  };

  const handleSave = async (data) => {
    try {
      if (selectedType) {
        await correspondenceTypeService.update(selectedType.id, data);
        showSnackbar('Tipo actualizado exitosamente', 'success');
      } else {
        await correspondenceTypeService.create({ ...data, companyId: parseInt(companyId) });
        showSnackbar('Tipo creado exitosamente', 'success');
      }
      setOpenModal(false);
      loadTypes();
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error al guardar tipo', 'error');
      throw error;
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Está seguro de eliminar este tipo de correspondencia?')) {
      try {
        await correspondenceTypeService.delete(id);
        showSnackbar('Tipo eliminado exitosamente', 'success');
        loadTypes();
      } catch (error) {
        showSnackbar('Error al eliminar tipo', 'error');
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Tipos de Correspondencia</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>
          Nuevo Tipo
        </Button>
      </Box>

      {types.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography>No hay tipos de correspondencia registrados</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Descripcion</TableCell>
                <TableCell>Area</TableCell>
                <TableCell>Dias de Expiracion</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {types.slice().sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((type) => (
                <TableRow
                  key={type.id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setDetailType(type)}
                >
                  <TableCell>{type.name}</TableCell>
                  <TableCell>{type.description || '—'}</TableCell>
                  <TableCell>{type.area?.name || '—'}</TableCell>
                  <TableCell>{type.expiration != null ? type.expiration : '—'}</TableCell>
                  <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                    <Box sx={{ display: 'inline-flex', gap: 0.25 }}>
                      <IconButton sx={{ color: '#5d5d60', width: 36, height: 36 }} onClick={(e) => { e.stopPropagation(); handleEdit(type); }}>
                        <EditOutlined sx={{ fontSize: 18 }} />
                      </IconButton>
                      <IconButton sx={{ color: '#5d5d60', width: 36, height: 36 }} onClick={(e) => { e.stopPropagation(); handleDelete(type.id); }}>
                        <DeleteOutlined sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <CorrespondenceTypeModalForm
        open={openModal}
        onClose={() => setOpenModal(false)}
        onSave={handleSave}
        correspondenceType={selectedType}
        companies={companies}
        preselectedCompanyId={companyId}
      />

      {/* Detail dialog */}
      <Dialog open={Boolean(detailType)} onClose={() => setDetailType(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
          <Mail color="primary" />
          <Typography variant="h6" component="span">{detailType?.name}</Typography>
          <IconButton onClick={() => setDetailType(null)} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {detailType?.area && (
              <Box>
                <Typography variant="caption" color="text.secondary">Área</Typography>
                <Typography variant="body1">{detailType.area.name}</Typography>
              </Box>
            )}
            {detailType?.expiration != null && (
              <Box>
                <Typography variant="caption" color="text.secondary">Días de vencimiento</Typography>
                <Typography variant="body1">{detailType.expiration} días</Typography>
              </Box>
            )}
            {detailType?.description && (
              <Box>
                <Typography variant="caption" color="text.secondary">Descripción</Typography>
                <Typography variant="body1">{detailType.description}</Typography>
              </Box>
            )}
            {detailType?.assignedUser && (
              <Box>
                <Typography variant="caption" color="text.secondary">Persona encargada</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <Person fontSize="small" color="primary" />
                  <Typography variant="body1">{detailType.assignedUser.name} ({detailType.assignedUser.email})</Typography>
                </Box>
              </Box>
            )}
            <Divider />
            <Box>
              <Typography variant="caption" color="text.secondary">Visibilidad</Typography>
              <Box sx={{ mt: 0.5 }}>
                <Chip
                  label={detailType?.public ? 'Público' : 'Privado'}
                  size="small"
                  color={detailType?.public ? 'success' : 'default'}
                  variant="outlined"
                />
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CompanyCorrespondenceTypes;
